import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera,
  Sparkles,
  Palette,
  Wand2,
  ArrowRight,
  X,
  Check,
  ChevronDown,
  Footprints,
  Printer,
  Share2,
  MessageCircle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

// ── Brand assets (Piczoo Brand Deck 2026) ───────────────────────────────────
// Pink logo published in customer-assets. Cached at CDN edge.
const PINK_LOGO =
  'https://customer-assets.emergentagent.com/job_278e4361-55f4-4949-a1d8-315c320d90c3/artifacts/jdjxf7b7_Piczoo%20Logo%20Pink.png';

// Four real bespoke booths from the deck's showcase, curated for the "belongs
// to the venue" narrative. Each caption is a design-language descriptor only —
// no venue names, cities or business terms disclosed.
const BOOTH_SHOWCASE = [
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_976b4fef-db16-4408-b58d-14730d3c9c2c/artifacts/5yafemuz_WhatsApp%20Image%202026-07-29%20at%2020.32.16.jpeg',
    title: 'Grecian Amphora',
    tag: 'Cobalt · Greek Key · Sunlit atrium',
  },
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_976b4fef-db16-4408-b58d-14730d3c9c2c/artifacts/b6sk8034_hf_20260729_145850_4ca5efe5-ffd9-4ac4-8103-0639fc1636be.webp',
    title: 'Mahogany Salon',
    tag: 'Oxblood · Brass · Velvet curtain',
  },
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_976b4fef-db16-4408-b58d-14730d3c9c2c/artifacts/8vy8zone_WhatsApp%20Image%202026-07-29%20at%2020.17.51%20%282%29.jpeg',
    title: 'Coastal Wave',
    tag: 'Teal · Cream ripple · Rounded shell',
  },
  {
    src: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_976b4fef-db16-4408-b58d-14730d3c9c2c/artifacts/mn9gs3jx_WhatsApp%20Image%202026-07-29%20at%2020.17.51%20%281%29.jpeg',
    title: 'Terracotta Sanctum',
    tag: 'Rust · Sand line-art · Warm interior',
  },
];

// The hero headline rotates through the venue types Piczoo serves. Cycles
// every ~2.4s with a soft crossfade. Keep the list short — the audience is
// premium-hospitality operators, not every category under the sun.
const VENUE_TYPES = ['Bar', 'Club', 'Brewery', 'Mall', 'Lounge', 'Cafe'];

const LandingPage = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    venue_name: '',
    contact_person: '',
    email: '',
    phone: '',
    city: '',
    location: '',
    average_footfall_per_month: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Rotating venue-type index for the hero headline.
  const [venueIdx, setVenueIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setVenueIdx((i) => (i + 1) % VENUE_TYPES.length),
      2400
    );
    return () => clearInterval(id);
  }, []);

  // Venue-partner benefits, reframed as an interior-design partnership per deck.
  const benefits = [
    {
      icon: <Palette className="w-5 h-5" />,
      title: 'Designed with your venue',
      desc:
        "Paint, curtain, screen — every finish is chosen with your space, not dropped on a stock template.",
    },
    {
      icon: <Wand2 className="w-5 h-5" />,
      title: 'Not a gadget. Furniture.',
      desc:
        'A rounded, non-boxed shell that hugs a corner. Guests walk past it a dozen times before noticing it is a booth.',
    },
    {
      icon: <Sparkles className="w-5 h-5" />,
      title: 'Four steps, no staff',
      desc:
        'Self-serve from walk-up to print. Your team keeps pouring. Your guests do the rest.',
    },
    {
      icon: <Camera className="w-5 h-5" />,
      title: 'A print in hand, a copy on their phone',
      desc:
        'Co-branded souvenir on the fridge tomorrow. A shareable digital lands on their feed tonight.',
    },
    {
      icon: <MessageCircle className="w-5 h-5" />,
      title: 'One voice, yours',
      desc:
        'Screen copy, fonts and colours mirror your brand — the booth speaks as if you designed the software too.',
    },
    {
      icon: <Printer className="w-5 h-5" />,
      title: 'We handle the boring bits',
      desc:
        'Install, paper, updates, remote diagnostics. You get analytics; we get the ladder.',
    },
  ];

  // Deck's canonical 4-step consumer journey.
  const guestSteps = [
    {
      num: '01',
      icon: <Footprints className="w-5 h-5" />,
      title: 'Walk up',
      desc:
        'Self-serve. Nothing to download, nobody to ask.',
    },
    {
      num: '02',
      icon: <Camera className="w-5 h-5" />,
      title: 'Shoot',
      desc: 'A few frames, guided on screen — done in under a minute.',
    },
    {
      num: '03',
      icon: <Printer className="w-5 h-5" />,
      title: 'Print',
      desc:
        'A co-branded print in hand before they sit back down.',
    },
    {
      num: '04',
      icon: <Share2 className="w-5 h-5" />,
      title: 'Share',
      desc:
        "The digital copy lands on their phone — and on their feed.",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        average_footfall_per_month:
          parseInt(formData.average_footfall_per_month, 10) || 0,
      };
      await axios.post(`${API}/contact`, payload);
      setSubmitted(true);
      setTimeout(() => {
        setShowContactForm(false);
        setSubmitted(false);
        setFormData({
          venue_name: '',
          contact_person: '',
          email: '',
          phone: '',
          city: '',
          location: '',
          average_footfall_per_month: '',
          notes: '',
        });
      }, 2000);
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.detail || "Couldn't send your message. Please try again."
      );
    }
    setSubmitting(false);
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.75, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
    }),
  };

  return (
    <div className="piczoo-bg text-noir-50 overflow-x-hidden font-sans" data-testid="piczoo-landing-page">
      {/* ── Navbar ───────────────────────────────────────────────────── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-noir-900/80 backdrop-blur-lg border-b border-white/[0.06]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a
              href="/"
              className="flex items-center gap-2.5"
              data-testid="nav-logo-link"
            >
              <img
                src={PINK_LOGO}
                alt="Piczoo"
                className="h-6 sm:h-7 w-auto object-contain"
              />
            </a>
            <button
              onClick={() => setShowContactForm(true)}
              className="pink-btn text-xs sm:text-sm tracking-wide uppercase px-5 py-2.5 sm:px-6 sm:py-2.5 rounded-full"
              data-testid="header-get-started-btn"
            >
              Partner with us
            </button>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section
        className="relative min-h-screen flex items-center piczoo-hero-bg overflow-hidden"
        data-testid="hero-section"
      >
        {/* Ambient blobs */}
        <div className="absolute -top-24 -left-24 w-[520px] h-[520px] bg-piczoo-500/[0.08] rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 -right-32 w-[480px] h-[480px] bg-piczoo-500/[0.05] rounded-full blur-[130px] pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-16 sm:pt-24 sm:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
            {/* Left: Copy */}
            <div className="lg:col-span-7">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={0}
                className="mb-6"
              >
                <span className="inline-flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.28em] uppercase text-piczoo-300 border border-piczoo-500/25 rounded-full px-4 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-piczoo-500 animate-pulse" />
                  Piczoo · Bespoke Photobooths
                </span>
              </motion.div>

              <motion.h1
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={1}
                className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight mb-6 sm:mb-8"
              >
                A photobooth
                <br className="hidden sm:block" />{' '}
                that <span className="pink-text-gradient">belongs</span>
                <br className="hidden sm:block" />{' '}
                <span className="whitespace-nowrap">
                  to your{' '}
                  <span
                    className="relative inline-block align-baseline"
                    style={{ minWidth: '6.5ch' }}
                    data-testid="hero-venue-rotator"
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={VENUE_TYPES[venueIdx]}
                        initial={{ opacity: 0, y: 24, filter: 'blur(6px)' }}
                        animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                        exit={{ opacity: 0, y: -24, filter: 'blur(6px)' }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="pink-text-gradient inline-block absolute left-0 top-0"
                      >
                        {VENUE_TYPES[venueIdx]}.
                      </motion.span>
                    </AnimatePresence>
                    {/* Invisible sizer so the container hugs the widest word
                        and the layout never jumps as words swap in and out. */}
                    <span className="invisible" aria-hidden="true">
                      Brewery.
                    </span>
                  </span>
                </span>
              </motion.h1>

              <motion.p
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={2}
                className="text-noir-100/75 text-base sm:text-lg max-w-xl leading-relaxed mb-9 sm:mb-11"
              >
                We do not park a gadget in the corner of your venue.
                We design a booth around your space — paint, curtain, screen
                and voice — so it reads like a piece of furniture you commissioned.
                <span className="block mt-3 text-noir-50/90 font-medium">
                  Guests get memories. You get a self-serve moment maker.
                </span>
              </motion.p>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={3}
                className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
              >
                <button
                  onClick={() => setShowContactForm(true)}
                  className="group pink-btn text-sm sm:text-base tracking-wide px-8 sm:px-10 py-3.5 sm:py-4 rounded-full"
                  data-testid="hero-get-started-btn"
                >
                  Design my booth
                  <ArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  href="#showcase"
                  className="text-xs sm:text-sm tracking-[0.18em] uppercase text-noir-100/60 hover:text-piczoo-300 transition-colors"
                  data-testid="hero-showcase-link"
                >
                  See the booths →
                </a>
              </motion.div>
            </div>

            {/* Right: hero booth still — pick the Grecian Amphora as it best
                embodies the "belongs to the venue" story (blue booth, matching wall). */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 relative flex justify-center lg:justify-end"
            >
              <div className="relative soft-lift">
                {/* Ambient glow */}
                <div className="absolute -inset-10 bg-piczoo-500/[0.14] rounded-full blur-[80px]" />
                {/* Frame */}
                <div className="relative overflow-hidden rounded-[28px] border border-piczoo-500/20 pink-glow bg-noir-800/40 backdrop-blur-sm">
                  <img
                    src={BOOTH_SHOWCASE[0].src}
                    alt="A Piczoo booth designed to match its venue — cobalt with Greek-key detail."
                    className="w-[280px] sm:w-[340px] lg:w-[380px] h-auto object-cover"
                    loading="eager"
                    data-testid="hero-booth-image"
                  />
                  {/* Caption chip */}
                  <div className="absolute bottom-4 left-4 right-4 bg-noir-900/80 backdrop-blur-md border border-piczoo-500/20 rounded-2xl px-4 py-3 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] tracking-[0.24em] uppercase text-piczoo-300">Case study</div>
                      <div className="text-sm font-medium text-noir-50">{BOOTH_SHOWCASE[0].title}</div>
                    </div>
                    <div className="hidden sm:block text-[10px] tracking-wide uppercase text-noir-100/50 text-right max-w-[130px]">
                      {BOOTH_SHOWCASE[0].tag}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase text-noir-100/40">Scroll</span>
          <ChevronDown className="w-4 h-4 text-piczoo-500/60 animate-bounce" />
        </motion.div>
      </section>

      {/* Greek-key divider — nod to the deck's Mykos motif */}
      <div className="greek-key-divider" aria-hidden="true" />

      {/* ── Showcase — "Every booth is a new brief" ─────────────────── */}
      <section
        id="showcase"
        className="py-20 sm:py-32 px-5 sm:px-8"
        data-testid="showcase-section"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeUp}
            className="max-w-3xl mb-14 sm:mb-20"
          >
            <span className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-piczoo-400 mb-4 block">
              Bespoke, not stock
            </span>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight mb-5">
              Every booth is a{' '}
              <span className="pink-text-gradient">new brief.</span>
            </h2>
            <p className="text-noir-100/70 text-base sm:text-lg leading-relaxed">
              No two Piczoo booths look alike. We pull the palette, texture and
              motif from the space they will live in — whether it&rsquo;s a bar, club,
              brewery, mall or lounge — then build a booth that could pass for a
              piece of the interior.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {BOOTH_SHOWCASE.map((booth, i) => (
              <motion.div
                key={booth.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                variants={fadeUp}
                custom={i}
                className="group brand-card rounded-2xl overflow-hidden"
                data-testid={`booth-showcase-${i}`}
              >
                <div className="aspect-[3/4] overflow-hidden bg-noir-800">
                  <img
                    src={booth.src}
                    alt={`Piczoo booth: ${booth.title}. ${booth.tag}.`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 sm:p-6">
                  <div className="text-[10px] tracking-[0.24em] uppercase text-piczoo-400 mb-1.5">
                    Case study {String(i + 1).padStart(2, '0')}
                  </div>
                  <h3 className="font-display font-semibold text-lg sm:text-xl text-noir-50 mb-1">
                    {booth.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-noir-100/60 leading-relaxed">
                    {booth.tag}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="wave-divider" aria-hidden="true" />

      {/* ── Four steps, no staff — deck's consumer flow ────────────── */}
      <section
        className="py-20 sm:py-32 px-5 sm:px-8 relative overflow-hidden"
        data-testid="steps-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-noir-900 via-noir-800/40 to-noir-900 pointer-events-none" />
        <div className="relative z-10 max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeUp}
            className="text-center max-w-2xl mx-auto mb-14 sm:mb-20"
          >
            <span className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-piczoo-400 mb-4 block">
              The guest experience
            </span>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight mb-5">
              Four steps.{' '}
              <span className="pink-text-gradient">No staff.</span>
            </h2>
            <p className="text-noir-100/70 text-base sm:text-lg leading-relaxed">
              From walk-up to feed post — the whole journey happens without
              your team lifting a finger.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            {guestSteps.map((s, i) => (
              <motion.div
                key={s.num}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                variants={fadeUp}
                custom={i}
                className="brand-card rounded-2xl p-6 sm:p-7 relative"
                data-testid={`guest-step-${i}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-11 h-11 rounded-full bg-piczoo-500/12 border border-piczoo-500/30 flex items-center justify-center text-piczoo-300">
                    {s.icon}
                  </div>
                  <span className="font-display font-semibold text-2xl text-piczoo-500/40 tracking-tight">
                    {s.num}
                  </span>
                </div>
                <h3 className="font-display font-semibold text-lg sm:text-xl text-noir-50 mb-2">
                  {s.title}
                </h3>
                <p className="text-sm text-noir-100/65 leading-relaxed">
                  {s.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="pink-divider" />

      {/* ── Why partner with Piczoo (venue-facing) ─────────────────── */}
      <section
        className="py-20 sm:py-32 px-5 sm:px-8"
        data-testid="benefits-section"
      >
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeUp}
            className="max-w-3xl mb-14 sm:mb-20"
          >
            <span className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-piczoo-400 mb-4 block">
              Why venues choose us
            </span>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight mb-5">
              Not a vendor.{' '}
              <span className="pink-text-gradient">A design partner.</span>
            </h2>
            <p className="text-noir-100/70 text-base sm:text-lg leading-relaxed">
              We are the only photobooth partner that treats your interior as
              a brief, not an afterthought. Everything below flows from that
              single choice.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {benefits.map((b, i) => (
              <motion.div
                key={b.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px' }}
                variants={fadeUp}
                custom={i}
                className="brand-card rounded-2xl p-6 sm:p-7"
                data-testid={`benefit-card-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-piczoo-500/12 border border-piczoo-500/25 flex items-center justify-center mb-5 text-piczoo-300">
                  {b.icon}
                </div>
                <h3 className="font-display font-semibold text-lg sm:text-xl text-noir-50 mb-2">
                  {b.title}
                </h3>
                <p className="text-sm text-noir-100/65 leading-relaxed">
                  {b.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div className="greek-key-divider" aria-hidden="true" />

      {/* ── Pull quote / positioning ─────────────────────────────────── */}
      <section className="py-20 sm:py-28 px-5 sm:px-8" data-testid="quote-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.blockquote
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeUp}
            className="font-display font-light text-2xl sm:text-3xl lg:text-4xl leading-[1.3] tracking-tight text-noir-50"
          >
            <span className="pink-text-gradient font-medium">
              &ldquo;A generic photobooth
            </span>{' '}
            looks like equipment parked in a corner. A Piczoo booth looks
            like something the interior designer signed off on.&rdquo;
          </motion.blockquote>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeUp}
            custom={1}
            className="mt-8 text-xs tracking-[0.3em] uppercase text-noir-100/40"
          >
            — Piczoo Design Studio
          </motion.div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section
        className="pb-24 pt-8 sm:pb-32 sm:pt-16 px-5 sm:px-8"
        data-testid="cta-section"
      >
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeUp}
            className="relative rounded-[32px] overflow-hidden brand-card p-10 sm:p-16 lg:p-20 text-center"
          >
            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-14 h-14 sm:w-20 sm:h-20 border-t-2 border-l-2 border-piczoo-500/50 rounded-tl-[32px]" />
            <div className="absolute bottom-0 right-0 w-14 h-14 sm:w-20 sm:h-20 border-b-2 border-r-2 border-piczoo-500/50 rounded-br-[32px]" />
            {/* Pink halo */}
            <div className="absolute -top-24 -right-24 w-72 h-72 bg-piczoo-500/[0.14] rounded-full blur-[80px] pointer-events-none" />

            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight mb-5 relative">
              Ready to see one <br className="sm:hidden" />
              <span className="pink-text-gradient">in your venue?</span>
            </h2>
            <p className="text-noir-100/70 mb-9 text-base sm:text-lg max-w-lg mx-auto leading-relaxed relative">
              Tell us about your space — bar, club, brewery, mall or lounge.
              We reply with a mood-board and a booth silhouette designed for it,
              usually within 48 hours.
            </p>
            <button
              onClick={() => setShowContactForm(true)}
              className="group pink-btn text-sm sm:text-base tracking-wide px-10 sm:px-12 py-3.5 sm:py-4 rounded-full relative"
              data-testid="cta-get-started-btn"
            >
              Start the brief
              <ArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="border-t border-white/[0.06] py-8 sm:py-10 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-5">
          <div className="flex items-center gap-3">
            <img
              src={PINK_LOGO}
              alt="Piczoo"
              className="h-5 sm:h-6 w-auto object-contain opacity-90"
            />
            <span className="text-noir-100/40 text-xs">
              &copy; 2026 · A photobooth that belongs to your venue.
            </span>
          </div>
          <a
            href="/admin/login"
            className="text-noir-100/40 text-xs hover:text-piczoo-300 transition-colors tracking-[0.18em] uppercase"
            data-testid="admin-login-footer-btn"
          >
            Admin
          </a>
        </div>
      </footer>

      {/* ── Contact Form Modal ───────────────────────────────────────── */}
      {showContactForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-noir-950/85 backdrop-blur-md"
          data-testid="contact-form-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="brand-card rounded-2xl p-6 sm:p-8 max-w-lg w-full relative max-h-[90vh] overflow-y-auto"
            data-testid="contact-form-modal"
          >
            <button
              onClick={() => setShowContactForm(false)}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-noir-100/50 hover:text-noir-50 transition-colors"
              aria-label="Close contact form"
              data-testid="close-contact-modal-btn"
            >
              <X className="w-5 h-5" />
            </button>

            {submitted ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 border border-piczoo-500/40 bg-piczoo-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-piczoo-400" />
                </div>
                <h3 className="font-display font-semibold text-xl sm:text-2xl text-noir-50 mb-2">
                  Brief received
                </h3>
                <p className="text-noir-100/60 text-sm">
                  We&rsquo;ll design a booth for your venue and be in touch within 48 hours.
                </p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <div className="text-[10px] tracking-[0.28em] uppercase text-piczoo-400 mb-2">
                    Start the brief
                  </div>
                  <h3 className="font-display font-semibold text-2xl sm:text-3xl text-noir-50 mb-1">
                    Tell us about your venue.
                  </h3>
                  <p className="text-noir-100/55 text-sm">
                    We reply with a mood-board and a booth silhouette designed for it.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <input
                    type="text"
                    placeholder="Venue Name"
                    required
                    maxLength={100}
                    value={formData.venue_name}
                    onChange={(e) =>
                      setFormData({ ...formData, venue_name: e.target.value })
                    }
                    className="w-full bg-transparent border-b border-white/10 focus:border-piczoo-500/70 py-3 text-sm text-noir-50 placeholder:text-noir-100/30 outline-none transition-colors"
                    data-testid="venue-name-input"
                  />
                  <input
                    type="text"
                    placeholder="Your Name"
                    required
                    maxLength={100}
                    value={formData.contact_person}
                    onChange={(e) =>
                      setFormData({ ...formData, contact_person: e.target.value })
                    }
                    className="w-full bg-transparent border-b border-white/10 focus:border-piczoo-500/70 py-3 text-sm text-noir-50 placeholder:text-noir-100/30 outline-none transition-colors"
                    data-testid="contact-person-input"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <input
                      type="email"
                      placeholder="Email"
                      required
                      maxLength={254}
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full bg-transparent border-b border-white/10 focus:border-piczoo-500/70 py-3 text-sm text-noir-50 placeholder:text-noir-100/30 outline-none transition-colors"
                      data-testid="email-input"
                    />
                    <input
                      type="tel"
                      placeholder="Phone"
                      required
                      maxLength={15}
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="w-full bg-transparent border-b border-white/10 focus:border-piczoo-500/70 py-3 text-sm text-noir-50 placeholder:text-noir-100/30 outline-none transition-colors"
                      data-testid="phone-input"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="City"
                    required
                    maxLength={80}
                    value={formData.city}
                    onChange={(e) =>
                      setFormData({ ...formData, city: e.target.value })
                    }
                    className="w-full bg-transparent border-b border-white/10 focus:border-piczoo-500/70 py-3 text-sm text-noir-50 placeholder:text-noir-100/30 outline-none transition-colors"
                    data-testid="city-input"
                  />
                  <input
                    type="text"
                    placeholder="Location / Area (optional)"
                    maxLength={200}
                    value={formData.location}
                    onChange={(e) =>
                      setFormData({ ...formData, location: e.target.value })
                    }
                    className="w-full bg-transparent border-b border-white/10 focus:border-piczoo-500/70 py-3 text-sm text-noir-50 placeholder:text-noir-100/30 outline-none transition-colors"
                    data-testid="location-input"
                  />
                  <input
                    type="number"
                    placeholder="Average Footfall per Month"
                    required
                    min="0"
                    max="1000000"
                    value={formData.average_footfall_per_month}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        average_footfall_per_month: e.target.value,
                      })
                    }
                    className="w-full bg-transparent border-b border-white/10 focus:border-piczoo-500/70 py-3 text-sm text-noir-50 placeholder:text-noir-100/30 outline-none transition-colors"
                    data-testid="footfall-input"
                  />
                  <textarea
                    placeholder="Tell us about the venue — mood, palette, footfall pattern (optional)"
                    rows={3}
                    maxLength={500}
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    className="w-full bg-transparent border-b border-white/10 focus:border-piczoo-500/70 py-3 text-sm text-noir-50 placeholder:text-noir-100/30 outline-none transition-colors resize-none"
                    data-testid="notes-input"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full pink-btn py-3.5 text-sm tracking-wide rounded-full disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="submit-contact-btn"
                  >
                    {submitting ? 'Sending…' : 'Send the brief'}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
